// ── Welle Z1: „Unterrichtszeiten deiner Schule“ (Einstellungen › Stundenplan) ──
// Tabelle Stunde · Beginn · Ende, „Schnell ausfüllen“ (Beginn, Stundenlänge, Pausen) und
// „Auf Standard zurücksetzen“. Gespeichert über PUT/DELETE /api/zeitraster; die Anzeige in
// Stundenplan und Heute folgt sofort (lib/zeitraster: setzeZeitrasterLokal).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, apiPut, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { globalNav } from '../../lib/nav';
import { MIcon } from '../../lib/micon';
import { LdFeld, LdEingabe, LdUhrzeit, LdAuswahl } from '../../components/ld/LdFenster';
import { EinstSpeichern } from './einstellungen-rahmen';
import { IconButton } from '../../components/ld/IconButton';
import {
  MAX_STUNDE, RasterEintrag, Zeitraster, fmtZeit, setzeZeitrasterLokal, useZeitraster, zeitZuMin,
} from '../../lib/zeitraster';
import './unterrichtszeiten.css';

const ZIEL_KEY = 'ld-einst-ziel';
const HINWEIS_KEY = 'ld-zeitraster-hinweis-zu';

/** Aus dem Stundenplan direkt zu diesem Abschnitt springen */
export function oeffneUnterrichtszeiten() {
  try { sessionStorage.setItem(ZIEL_KEY, 'unterrichtszeiten'); } catch { /* egal */ }
  globalNav('einstellungen');
}

/** Dezenter, schließbarer Hinweis im Stundenplan, solange das Standard-Raster gilt */
export function ZeitrasterHinweis() {
  const [zu, setZu] = useState(() => { try { return localStorage.getItem(HINWEIS_KEY) === '1'; } catch { return false; } });
  if (zu) return null;
  return (
    <div className="ld-zr-hinweis" role="note">
      <MIcon name="schedule" size={18} />
      <span className="ld-zr-hinweis__text">
        Stimmen die Zeiten?{' '}
        <button type="button" className="ld-zr-hinweis__link" onClick={oeffneUnterrichtszeiten}>Unterrichtszeiten deiner Schule einstellen</button>
      </span>
      <IconButton variant="bare" size={32} label="Hinweis schließen"
        onClick={() => { setZu(true); try { localStorage.setItem(HINWEIS_KEY, '1'); } catch { /* egal */ } }}>
        <MIcon name="close" size={16} />
      </IconButton>
    </div>
  );
}

type Zeile = { beginn: string; ende: string };
const zeilenAus = (raster: RasterEintrag[]): Zeile[] =>
  Array.from({ length: MAX_STUNDE }, (_, i) => {
    const r = raster.find(x => x.stunde === i + 1);
    return { beginn: r?.beginn ?? '', ende: r?.ende ?? '' };
  });

/** Clientseitige Prüfung (dieselben Regeln wie der Server) → Fehlertext je Zeile */
function pruefeZeilen(zeilen: Zeile[]): { raster: RasterEintrag[]; fehler: Record<number, string> } {
  const fehler: Record<number, string> = {};
  const raster: RasterEintrag[] = [];
  zeilen.forEach((z, i) => {
    const nr = i + 1;
    if (!z.beginn && !z.ende) return;
    if (!z.beginn || !z.ende) { fehler[nr] = 'Beginn und Ende angeben — oder beide leer lassen.'; return; }
    if (zeitZuMin(z.beginn) >= zeitZuMin(z.ende)) { fehler[nr] = 'Das Ende muss nach dem Beginn liegen.'; return; }
    raster.push({ stunde: nr, beginn: z.beginn, ende: z.ende });
  });
  for (let i = 1; i < raster.length; i++) {
    const a = raster[i - 1], b = raster[i];
    if (zeitZuMin(b.beginn) < zeitZuMin(a.ende) && !fehler[b.stunde]) fehler[b.stunde] = `Beginnt, bevor die ${a.stunde}. Stunde endet.`;
  }
  if (raster.length === 0 && Object.keys(fehler).length === 0) fehler[1] = 'Mindestens eine Stunde eintragen.';
  return { raster, fehler };
}

type Pause = { nach: number; minuten: number };

export function Unterrichtszeiten() {
  const z = useZeitraster();
  const [zeilen, setZeilen] = useState<Zeile[]>(() => zeilenAus(z.raster));
  const [gespeichert, setGespeichert] = useState<string>(() => JSON.stringify(zeilenAus(z.raster)));
  const [speichert, setSpeichert] = useState(false);
  const [schnellOffen, setSchnellOffen] = useState(false);
  const [start, setStart] = useState('07:45');
  const [laenge, setLaenge] = useState(45);
  const [anzahl, setAnzahl] = useState(8);
  const [kurzPause, setKurzPause] = useState(5);
  const [pausen, setPausen] = useState<Pause[]>([{ nach: 2, minuten: 20 }, { nach: 4, minuten: 15 }]);
  const ref = useRef<HTMLDivElement | null>(null);

  // Server-Stand übernehmen, solange nichts geändert wurde
  const unveraendert = JSON.stringify(zeilen) === gespeichert;
  const zKey = JSON.stringify(z.raster);
  useEffect(() => {
    const neu = JSON.stringify(zeilenAus(z.raster));
    if (unveraendert) { setZeilen(zeilenAus(z.raster)); }
    setGespeichert(neu);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zKey]);

  // Sprung aus dem Stundenplan („Zeiten ändern“ / Hinweis)
  useEffect(() => {
    try {
      if (sessionStorage.getItem(ZIEL_KEY) === 'unterrichtszeiten') {
        sessionStorage.removeItem(ZIEL_KEY);
        window.setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    } catch { /* egal */ }
  }, []);

  const { raster, fehler } = useMemo(() => pruefeZeilen(zeilen), [zeilen]);
  const hatFehler = Object.keys(fehler).length > 0;

  function setze(i: number, feld: keyof Zeile, wert: string) {
    setZeilen(prev => prev.map((zz, j) => j === i ? { ...zz, [feld]: wert } : zz));
  }

  function schnellAusfuellen() {
    let t = zeitZuMin(start);
    const neu: Zeile[] = Array.from({ length: MAX_STUNDE }, () => ({ beginn: '', ende: '' }));
    for (let nr = 1; nr <= Math.min(anzahl, MAX_STUNDE); nr++) {
      if (t + laenge >= 24 * 60) break;
      neu[nr - 1] = { beginn: fmtZeit(t), ende: fmtZeit(t + laenge) };
      const pause = pausen.find(p => p.nach === nr)?.minuten ?? kurzPause;
      t += laenge + pause;
    }
    setZeilen(neu);
    toast('Tabelle ausgefüllt — prüfen und speichern', 'info');
  }

  async function speichern() {
    if (hatFehler) return;
    setSpeichert(true);
    try {
      const r = await apiPut<Zeitraster>('/api/zeitraster', { raster });
      setzeZeitrasterLokal(r);
      setZeilen(zeilenAus(r.raster));
      setGespeichert(JSON.stringify(zeilenAus(r.raster)));
      toast('Unterrichtszeiten gespeichert');
    } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); } finally { setSpeichert(false); }
  }

  async function zuruecksetzen() {
    const ok = await confirm('Die Unterrichtszeiten werden auf den Standard (Hessen, 45 Minuten, 1. Stunde 07:45) zurückgesetzt.', {
      title: 'Auf Standard zurücksetzen?', confirmLabel: 'Zurücksetzen',
    });
    if (!ok) return;
    try {
      const r = await apiFetch<Zeitraster>('/api/zeitraster', { method: 'DELETE' });
      setzeZeitrasterLokal(r);
      setZeilen(zeilenAus(r.raster));
      setGespeichert(JSON.stringify(zeilenAus(r.raster)));
      toast('Standard-Zeiten wiederhergestellt');
    } catch (e) { zeigeApiFehler(e, 'Zurücksetzen fehlgeschlagen'); }
  }

  // Pause bis zur nächsten eingetragenen Stunde (klein rechts in der Zeile)
  const pauseNach = (i: number): number | null => {
    const a = zeilen[i]; if (!a.beginn || !a.ende) return null;
    const b = zeilen.slice(i + 1).find(x => x.beginn && x.ende); if (!b) return null;
    const p = zeitZuMin(b.beginn) - zeitZuMin(a.ende);
    return p > 0 ? p : null;
  };

  return (
    <div ref={ref} id="unterrichtszeiten" className="ld-zr">
      {z.standard && <p className="ld-fe-text">Gerade gilt der Standard (Hessen, 45 Minuten).</p>}

      <div className="ld-zr__schnell-block">
        <button type="button" className="ld-zr__schnell-toggle" aria-expanded={schnellOffen} onClick={() => setSchnellOffen(o => !o)}>
          <MIcon name="bolt" size={18} />
          <span>Schnell ausfüllen</span>
          <small>{laenge} Min., Start {start}</small>
          <MIcon name={schnellOffen ? 'expand_less' : 'expand_more'} size={20} className="ld-zr__chev" />
        </button>
        {schnellOffen && (
          <div className="ld-zr__schnell">
            <div className="ld-zr__schnell-grid">
              <LdFeld label="Beginn 1. Stunde">
                <LdUhrzeit kompakt aria-label="Beginn 1. Stunde" placeholder="07:45" value={start} onChange={v => v && setStart(v)} />
              </LdFeld>
              <LdFeld label="Stundenlänge">
                <LdEingabe type="number" min={20} max={120} aria-label="Stundenlänge" value={laenge} className="ld-es-zahl"
                  onChange={e => setLaenge(Math.max(1, Number(e.target.value) || 45))} rechts={<span className="ld-es-einheit">Min.</span>} />
              </LdFeld>
              <LdFeld label="Stunden am Tag">
                <LdEingabe type="number" min={1} max={MAX_STUNDE} aria-label="Stunden am Tag" value={anzahl} className="ld-es-zahl"
                  onChange={e => setAnzahl(Math.min(MAX_STUNDE, Math.max(1, Number(e.target.value) || 1)))} />
              </LdFeld>
              <LdFeld label="Sonst Pause">
                <LdEingabe type="number" min={0} max={60} aria-label="Sonst Pause" value={kurzPause} className="ld-es-zahl"
                  onChange={e => setKurzPause(Math.max(0, Number(e.target.value) || 0))} rechts={<span className="ld-es-einheit">Min.</span>} />
              </LdFeld>
            </div>
            <LdFeld label="Große Pausen">
              <div className="ld-zr__pausen">
                {pausen.map((p, i) => (
                  <div key={i} className="ld-zr__pause">
                    <span>nach der</span>
                    <span className="ld-zr__pause-nr">
                      <LdAuswahl aria-label="Pause nach Stunde" value={p.nach}
                        onChange={e => setPausen(prev => prev.map((x, j) => j === i ? { ...x, nach: Number(e.target.value) } : x))}>
                        {Array.from({ length: MAX_STUNDE - 1 }, (_, k) => <option key={k + 1} value={k + 1}>{k + 1}.</option>)}
                      </LdAuswahl>
                    </span>
                    <span>Stunde</span>
                    <span className="ld-zr__pause-min">
                      <LdEingabe type="number" min={0} max={90} aria-label="Pausenlänge in Minuten" value={p.minuten} className="ld-es-zahl"
                        onChange={e => setPausen(prev => prev.map((x, j) => j === i ? { ...x, minuten: Math.max(0, Number(e.target.value) || 0) } : x))}
                        rechts={<span className="ld-es-einheit">Min.</span>} />
                    </span>
                    <button type="button" className="ld-es-fenster__weg" aria-label="Pause entfernen" onClick={() => setPausen(prev => prev.filter((_, j) => j !== i))}>
                      <MIcon name="close" size={16} />
                    </button>
                  </div>
                ))}
                {pausen.length < 4 && (
                  <button type="button" className="ld-kl-link" onClick={() => setPausen(prev => [...prev, { nach: Math.min(MAX_STUNDE - 1, (prev[prev.length - 1]?.nach ?? 0) + 2), minuten: 15 }])}>
                    <MIcon name="add" size={17} />Pause hinzufügen
                  </button>
                )}
              </div>
            </LdFeld>
            <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" onClick={schnellAusfuellen}>Tabelle ausfüllen</button>
          </div>
        )}
      </div>

      <div className="ld-zr__tabelle" role="table" aria-label="Unterrichtszeiten">
        {zeilen.map((zz, i) => {
          const nr = i + 1;
          const f = fehler[nr];
          const leer = !zz.beginn && !zz.ende;
          const pause = pauseNach(i);
          return (
            <div key={nr} className={`ld-zr__zeile${leer ? ' ist-leer' : ''}${f ? ' hat-fehler' : ''}`} role="row">
              <span className="ld-zr__nr" role="cell">{nr}.</span>
              <span role="cell" className="ld-zr__zeit"><LdUhrzeit kompakt aria-label={`${nr}. Stunde Beginn`} placeholder="leer" value={zz.beginn} onChange={v => setze(i, 'beginn', v)} /></span>
              <span className="ld-zr__bis" aria-hidden="true">bis</span>
              <span role="cell" className="ld-zr__zeit"><LdUhrzeit kompakt aria-label={`${nr}. Stunde Ende`} placeholder="leer" value={zz.ende} onChange={v => setze(i, 'ende', v)} /></span>
              <span role="cell" className="ld-zr__pause-info">{pause ? `${pause} Min. Pause` : ''}</span>
              {f && <span className="ld-zr__fehler" role="alert">{f}</span>}
            </div>
          );
        })}
      </div>
      <p className="ld-zr__leer-hinweis">Stunden, die es an deiner Schule nicht gibt, einfach leer lassen.</p>
      {!z.standard && <button type="button" className="ld-kl-link" style={{ alignSelf: 'flex-start' }} onClick={zuruecksetzen}>Auf Standard zurücksetzen</button>}

      <EinstSpeichern onClick={speichern} laden={speichert} aus={unveraendert || hatFehler} />
    </div>
  );
}
